import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChevronLeft, Calendar, MapPin, Globe, Users, Lock } from 'lucide-react';
import { eventService } from '../services/dataService';
import type { ConferenceEvent } from '@conference/shared';
import { resolveEventModules } from '@conference/shared';
import { notify } from '../utils/notifications';
import { buildEventPreviewDescription } from '../utils/eventFeatureCopy';

export default function EventPreviewScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<ConferenceEvent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    eventService.getById(id)
      .then(setEvent)
      .catch(() => notify.error('Could not load event'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ backgroundColor: 'var(--app-bg)' }}>
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="size-full flex flex-col items-center justify-center px-6" style={{ backgroundColor: 'var(--app-bg)' }}>
        <p style={{ color: 'var(--app-text-muted)' }}>Event not found.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm" style={{ color: 'var(--app-accent)' }}>Go back</button>
      </div>
    );
  }

  const startDate = new Date(`${event.date_start}T00:00:00`);
  const endDate = new Date(`${event.date_end}T00:00:00`);
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
  const modules = resolveEventModules(event.app_modules);
  const aiModule = modules.find((module) => module.key === 'ai_copilot');

  return (
    <div className="size-full flex flex-col overflow-y-auto" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Cover image */}
      {event.image_url ? (
        <div className="relative h-48 flex-shrink-0">
          <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, var(--app-bg))' }} />
          <button
            onClick={() => navigate(-1)}
            className="absolute top-12 left-4 flex items-center gap-1 text-white active:opacity-70 transition-opacity"
          >
            <ChevronLeft size={18} /> Back
          </button>
        </div>
      ) : (
        <div className="app-screen-header pt-14 px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 mb-6 active:opacity-70 transition-opacity"
            style={{ color: 'var(--app-text-muted)' }}
          >
            <ChevronLeft size={18} />
            <span className="text-sm">Back</span>
          </button>
        </div>
      )}

      <div className="px-6 pb-8">
        <h1 className="text-2xl font-bold tracking-tight mb-4" style={{ color: 'var(--app-text)' }}>
          {event.name}
        </h1>

        {/* Meta */}
        <div className="flex flex-col gap-2 mb-6">
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted)' }}>
            <Calendar size={14} />
            <span>{startDate.toLocaleDateString('en-US', opts)} – {endDate.toLocaleDateString('en-US', opts)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-text-muted)' }}>
            <MapPin size={14} />
            <span>{event.location}{event.venue ? ` · ${event.venue}` : ''}</span>
          </div>
          {event.website && (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--app-accent)' }}>
              <Globe size={14} />
              <a href={event.website} target="_blank" rel="noopener noreferrer" className="underline">
                {event.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
        </div>

        {event.description && (
          <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--app-text-muted)' }}>
            {event.description}
          </p>
        )}

        {/* Viewer notice */}
        <div
          className="rounded-xl p-4 mb-6 flex items-start gap-3"
          style={{ backgroundColor: 'var(--app-card-bg)', border: '1px solid var(--app-border)' }}
        >
          <Users size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--app-accent)' }} />
          <div>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--app-text)' }}>Public Preview Mode</p>
            <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
              {buildEventPreviewDescription(modules)}
            </p>
          </div>
        </div>

        {/* Join CTA */}
        <button
          onClick={() => navigate('/join')}
          className="h-14 w-full rounded-xl text-white text-base font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform mb-3"
          style={{ backgroundColor: 'var(--app-accent-solid)', boxShadow: '0 8px 16px var(--app-accent-glow)' }}
        >
          Join with Invite Code
        </button>

        {/* AI lock note */}
        {aiModule?.enabled && (
          <div className="flex items-center justify-center gap-2 text-xs" style={{ color: 'var(--app-text-subtle)' }}>
            <Lock size={11} />
            {aiModule.label} requires membership
          </div>
        )}
      </div>
    </div>
  );
}
